import { Component, inject, Inject, OnInit } from '@angular/core'
import { Call, CallAccessLevel, CallPublicLinkAvailability, CallsService } from '../../calls.service'
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogModule, MatDialogTitle } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatCheckbox } from '@angular/material/checkbox'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatDivider } from '@angular/material/divider'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field'
import { MatAutocompleteModule, MatOption } from '@angular/material/autocomplete'
import { MatSelect } from '@angular/material/select'
import { filter, map } from 'rxjs'
import { ToastService } from '../../toast.service'
import { MatInput } from '@angular/material/input'
import { User, UsersService } from '../../users.service'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { MatChip } from '@angular/material/chips'
import { CdkCopyToClipboard } from '@angular/cdk/clipboard'
import { constructNameForUser, isEmailValid } from '../../utils'
import { SpinnerComponent } from '../../spinner/spinner.component'
import { UserNamePipe } from '../../user-name.pipe'

@Component({
  selector: 'app-share-call-dialog',
  standalone: true,
  imports: [
    MatButton,
    MatCheckbox,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    TranslateModule,
    MatDialogClose,
    MatDivider,
    FormsModule,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatInput,
    MatAutocompleteModule,
    MatHint,
    MatIcon,
    MatTooltip,
    MatDialogModule,
    MatChip,
    CdkCopyToClipboard,
    SpinnerComponent,
    UserNamePipe,
  ],
  templateUrl: './share-call-dialog.component.html',
  styleUrl: './share-call-dialog.component.scss',
})
export class ShareCallDialogComponent implements OnInit {
  callsService = inject(CallsService)
  toastService = inject(ToastService)
  usersService = inject(UsersService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)

  updateShareLevelMutation = this.callsService.updateCallAccessLevel(this.call.id)
  shareToUserMutation = this.callsService.shareToUser(this.call.id)
  shareByEmailMutation = this.callsService.shareByEmail(this.call.id)
  removeShareMutation = this.callsService.removeShareMutation(this.call.id)
  publicLinkAccessMutation = this.callsService.publicLinkAccessLevelMutation()

  canEdit = false
  showShareForm = false
  userToShareTo: User | null = null
  firstName = ''
  lastName = ''
  email = ''
  level: CallAccessLevel = 'VIEWER'
  notifyByEmail = true
  filteredOrganizationUsers: User[] = []
  organizationUsers: User[] = []
  loading = false

  constructor(@Inject(MAT_DIALOG_DATA) public call: Call) {}

  ngOnInit(): void {
    this.initializeOrganizationUsers()
    this.canEdit = this.call.accessLevel === 'FULL_ACCESS'
  }

  async updateShareLevel(sharedUserId: number, level: CallAccessLevel) {
    // TODO Add error handling and loading state
    await this.updateShareLevelMutation.mutateAsync({ sharedUserId, level })
    this.resetShareForm()
    this.toastService.show('CALLS.SHARE_DIALOG.UPDATED_NOTIFICATION')
  }

  filterOrganizationUsers(input: string): void {
    this.filteredOrganizationUsers = this.organizationUsers
      .filter((user) => !this.call.users.map((callUser) => callUser.id).includes(user.id))
      .filter((user) => !this.call.shares.map((share) => share.sharedUserId).includes(user.id))
      .filter((user) => user.id !== this.usersService.currentUser$.value?.id)
      .filter((user) => (constructNameForUser(user) + user.email).includes(input))
  }

  displayUserOnAutocomplete(user: User): string {
    return user !== null && typeof user === 'object' ? constructNameForUser(user) : ''
  }

  async shareCall(): Promise<void> {
    this.loading = true
    const userToShareTo = this.userToShareTo || (this.organizationUsers.find((user) => user.email === this.email) ?? null)
    const share =
      userToShareTo !== null
        ? await this.shareToUserMutation.mutateAsync({
            sharedUserId: userToShareTo.id,
            level: this.level,
            notifyByEmail: this.notifyByEmail,
          })
        : await this.shareByEmailMutation.mutateAsync({
            email: this.email,
            firstName: this.firstName,
            lastName: this.lastName,
            level: this.level,
          })
    this.call.shares.push(share)
    this.resetShareForm()
    this.toastService.show('CALLS.SHARE_DIALOG.SHARED_NOTIFICATION')
    this.loading = false
  }

  async removeShare(sharedUser: User) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.SHARE_DIALOG.REMOVE_SHARE.TITLE'),
        description: this.translateService.instant('CALLS.SHARE_DIALOG.REMOVE_SHARE.DESCRIPTION', { userName: constructNameForUser(sharedUser) }),
      },
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.removeShareMutation.mutateAsync(sharedUser.id)
        this.call.shares = this.call.shares.filter((share) => share.sharedUserId !== sharedUser.id)
        this.toastService.show('CALLS.SHARE_DIALOG.SHARE_REMOVED_NOTIFICATION')
      }
    })
  }

  async updatePublicShareLevel(level: CallAccessLevel) {
    await this.publicLinkAccessMutation.mutateAsync({ callId: this.call.id, level: level, availability: undefined })
    this.call.publicLinkAccessLevel = level
    this.toastService.show('CALLS.SHARE_DIALOG.PUBLIC_SHARE_UPDATED')
  }

  async updatePublicLinkAvailability(availability: CallPublicLinkAvailability) {
    if (availability === 'ANYONE_WITH_ACCOUNT') {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.translateService.instant('CALLS.SHARE_DIALOG.CONFIRM_PUBLIC_ACCESS.TITLE'),
          description: this.translateService.instant('CALLS.SHARE_DIALOG.CONFIRM_PUBLIC_ACCESS.DESCRIPTION'),
        },
      })

      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          await this.actuallyUpdatePublicLinkAvailability(availability)
        } else {
          this.resetPublicLinkAvailability()
        }
      })
    } else {
      await this.actuallyUpdatePublicLinkAvailability(availability)
    }
  }

  private async actuallyUpdatePublicLinkAvailability(availability: CallPublicLinkAvailability) {
    await this.publicLinkAccessMutation.mutateAsync({ callId: this.call.id, level: undefined, availability: availability })
    this.call.publicLinkAvailability = availability
    this.toastService.show('CALLS.SHARE_DIALOG.PUBLIC_SHARE_UPDATED')
  }

  isValidInput() {
    const isUserSelected = this.userToShareTo !== null
    const areUserCreationDetailsProvided = this.isValidEmail && this.firstName !== '' && this.lastName !== ''
    const isValidInput = isUserSelected || areUserCreationDetailsProvided

    const isAlreadySharedToUserByUserId = this.userToShareTo !== null && this.call.shares.map((share) => share.sharedUser.id).includes(this.userToShareTo.id)
    const isAlreadySharedToUserByEmail = this.call.shares.map((share) => share.sharedUser.email).includes(this.email)
    const isAlreadySharedToUser = isAlreadySharedToUserByUserId || isAlreadySharedToUserByEmail

    return isValidInput && !isAlreadySharedToUser
  }

  get displayNameInput() {
    return this.call.allowThirdPartySharing && this.userToShareTo === null && this.isValidEmail && !this.emailMatchesExistingUser
  }

  get emailMatchesExistingUser() {
    return this.organizationUsers.map((user) => user.email).includes(this.email)
  }

  get isValidEmail() {
    return isEmailValid(this.email)
  }

  private resetShareForm() {
    this.showShareForm = false
    this.email = ''
    this.firstName = ''
    this.lastName = ''
    this.level = 'VIEWER'
  }

  private initializeOrganizationUsers() {
    this.usersService
      .getOrganizationUsers()
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((users) => (this.organizationUsers = users!))
  }

  get publicLink() {
    return `${window.location.origin}${window.location.pathname}?secret=${this.call.publicLinkSecret}`
  }

  private resetPublicLinkAvailability() {
    // If the user cancels the confirmation dialog for "ANYONE_WITH_ACCOUNT", the wrong selection remains on the selection element
    const initialValue = this.call.publicLinkAvailability
    this.call.publicLinkAvailability = 'ANYONE_WITH_ACCOUNT'
    setTimeout(() => (this.call.publicLinkAvailability = initialValue), 0)
  }
}
